import React, { useEffect, useRef, useState } from 'react'
import {
  Alert, Badge, Button, Card, Col, Container, Form,
  InputGroup, Row, Spinner, Table
} from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { formatDateTimeTH, formatDateTH } from '../utils/dateUtils'

const fmt = (n) =>
  Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const EMPTY_FORM = {
  transaction_type: 'deposit',
  amount: '',
  note: '',
  occurred_date: new Date(),
}

export default function BankTransactions() {
  const [query,        setQuery]        = useState('')
  const [hints,        setHints]        = useState([])
  const [hintLoading,  setHintLoading]  = useState(false)
  const [selectedAcc,  setSelectedAcc]  = useState(null)

  const [transactions, setTransactions] = useState([])
  const [txLoading,    setTxLoading]    = useState(false)

  const [form,         setForm]         = useState(EMPTY_FORM)
  const [saving,       setSaving]       = useState(false)
  const [saveError,    setSaveError]    = useState('')
  const [saveSuccess,  setSaveSuccess]  = useState('')

  const debounceRef = useRef(null)

  const searchAccounts = (q) => {
    clearTimeout(debounceRef.current)
    if (q.trim().length < 1) { setHints([]); return }
    setHintLoading(true)
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axiosInstance.get(`/api/bank/accounts/search?q=${encodeURIComponent(q)}`)
        setHints(res.data.success ? res.data.data : [])
      } catch (_) {
        setHints([])
      } finally {
        setHintLoading(false)
      }
    }, 300)
  }

  const selectAccount = async (acc) => {
    setQuery(`${acc.account_no} – ${acc.st_name}`)
    setHints([])
    setSelectedAcc(acc)
    fetchTransactions(acc.account_id)
  }

  const fetchTransactions = async (account_id) => {
    setTxLoading(true)
    try {
      const res = await axiosInstance.get(`/api/bank/transactions?account_id=${account_id}`)
      setTransactions(res.data.success ? res.data.data : [])
    } catch (_) {
      setTransactions([])
    } finally {
      setTxLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!selectedAcc) { setSaveError('กรุณาเลือกบัญชีก่อน'); return }
    if (!form.amount || parseFloat(form.amount) <= 0) { setSaveError('กรุณาระบุจำนวนเงินที่ถูกต้อง'); return }

    setSaving(true)
    setSaveError('')
    setSaveSuccess('')
    try {
      const body = {
        account_id:       selectedAcc.account_id,
        transaction_type: form.transaction_type,
        amount:           parseFloat(form.amount),
        note:             form.note,
        occurred_date:    form.occurred_date.toISOString().slice(0, 10),
      }
      const res = await axiosInstance.post('/api/bank/transactions', body)
      if (res.data.success) {
        setSaveSuccess(
          `บันทึกสำเร็จ – ยอดคงเหลือใหม่: ฿${fmt(res.data.data.balance_after)}`
        )
        setForm(EMPTY_FORM)
        // refresh account balance + transaction list
        const accRes = await axiosInstance.get(`/api/bank/accounts/${selectedAcc.account_id}`)
        if (accRes.data.success) setSelectedAcc(accRes.data.data)
        fetchTransactions(selectedAcc.account_id)
      }
    } catch (err) {
      setSaveError(err.response?.data?.message || 'บันทึกรายการไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <h4 className="main-title mb-4">
            <i className="ri-exchange-funds-line me-2 text-primary" />
            บันทึกรายการฝาก/ถอน
          </h4>

          <Row className="g-3">
            {/* Left: search + form */}
            <Col xl={5}>
              {/* Account search */}
              <Card className="card-one mb-3">
                <Card.Header className="pb-0"><Card.Title as="h6" className="mb-0">ค้นหาบัญชี</Card.Title></Card.Header>
                <Card.Body className="p-3">
                  <div className="position-relative">
                    <InputGroup size="sm">
                      <InputGroup.Text>
                        {hintLoading ? <Spinner size="sm" /> : <i className="ri-search-line" />}
                      </InputGroup.Text>
                      <Form.Control
                        placeholder="เลขบัญชี, ชื่อนักเรียน, หรือรหัสนักเรียน"
                        value={query}
                        onChange={e => { setQuery(e.target.value); searchAccounts(e.target.value) }}
                        autoComplete="off"
                      />
                    </InputGroup>
                    {hints.length > 0 && (
                      <div
                        className="border rounded bg-white shadow position-absolute w-100"
                        style={{ zIndex: 1050, top: '100%' }}
                      >
                        {hints.map(a => (
                          <div
                            key={a.account_id}
                            className="px-3 py-2 fs-sm"
                            style={{ cursor: 'pointer' }}
                            onClick={() => selectAccount(a)}
                            onMouseEnter={e => e.currentTarget.classList.add('bg-light')}
                            onMouseLeave={e => e.currentTarget.classList.remove('bg-light')}
                          >
                            <code className="me-2">{a.account_no}</code>
                            {a.st_name}
                            <span className="float-end text-success fw-semibold">฿{fmt(a.current_balance)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {selectedAcc && (
                    <div className="mt-3 p-2 border rounded bg-light fs-sm">
                      <div className="fw-semibold mb-1">{selectedAcc.st_name}</div>
                      <div className="text-muted mb-1">เลขบัญชี: <code>{selectedAcc.account_no}</code></div>
                      <div className="d-flex justify-content-between">
                        <span>ยอดคงเหลือ:</span>
                        <span className="fw-bold text-primary">฿{fmt(selectedAcc.current_balance)}</span>
                      </div>
                      <div>
                        <Badge bg={selectedAcc.account_status === 'active' ? 'success' : 'secondary'}>
                          {selectedAcc.account_status === 'active' ? 'เปิดอยู่' : 'ปิดแล้ว'}
                        </Badge>
                      </div>
                    </div>
                  )}
                </Card.Body>
                <Card.Header className="pb-0"><Card.Title as="h6" className="mb-0">บันทึกรายการ</Card.Title></Card.Header>
                <Card.Body className="p-3">
                  {saveError   && <Alert variant="danger"  className="py-2 fs-sm">{saveError}</Alert>}
                  {saveSuccess && <Alert variant="success" className="py-2 fs-sm">{saveSuccess}</Alert>}

                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-2">
                      <Form.Label className="fs-sm mb-1">ประเภทรายการ</Form.Label>
                      <div className="d-flex gap-3">
                        {['deposit', 'withdrawal'].map(t => (
                          <Form.Check
                            key={t}
                            type="radio"
                            label={t === 'deposit' ? '💰 ฝาก' : '💸 ถอน'}
                            id={`type-${t}`}
                            name="transaction_type"
                            value={t}
                            checked={form.transaction_type === t}
                            onChange={() => setForm(f => ({ ...f, transaction_type: t }))}
                          />
                        ))}
                      </div>
                    </Form.Group>

                    <Form.Group className="mb-2">
                      <Form.Label className="fs-sm mb-1">จำนวนเงิน (บาท)</Form.Label>
                      <InputGroup size="sm">
                        <InputGroup.Text>฿</InputGroup.Text>
                        <Form.Control
                          type="number"
                          min="0.01"
                          step="0.01"
                          placeholder="0.00"
                          value={form.amount}
                          onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                          required
                        />
                      </InputGroup>
                    </Form.Group>
                    {/* Transaction form */}
                    <Form.Group className="mb-2">
                      <Form.Label className="fs-sm mb-1">วันที่เกิดรายการ</Form.Label>
                      <div>
                        <DatePicker
                          selected={form.occurred_date}
                          onChange={d => setForm(f => ({ ...f, occurred_date: d }))}
                          dateFormat="dd/MM/yyyy"
                          className="form-control form-control-sm"
                        />
                      </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="fs-sm mb-1">หมายเหตุ</Form.Label>
                      <Form.Control
                        size="sm"
                        as="textarea"
                        rows={2}
                        placeholder="หมายเหตุ (ไม่บังคับ)"
                        value={form.note}
                        onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                      />
                    </Form.Group>

                    <Button
                      type="submit"
                      variant={form.transaction_type === 'deposit' ? 'success' : 'danger'}
                      size="sm"
                      className="w-100"
                      disabled={saving || !selectedAcc || selectedAcc.account_status !== 'active'}
                    >
                      {saving
                        ? <><Spinner size="sm" className="me-1" />กำลังบันทึก...</>
                        : form.transaction_type === 'deposit'
                          ? <><i className="ri-arrow-down-circle-line me-1" />บันทึกฝาก</>
                          : <><i className="ri-arrow-up-circle-line me-1" />บันทึกถอน</>
                      }
                    </Button>
                    {selectedAcc?.account_status !== 'active' && (
                      <div className="text-danger fs-sm mt-1 text-center">บัญชีนี้ถูกปิดแล้ว</div>
                    )}
                  </Form>
                </Card.Body>
              </Card>
            </Col>

            {/* Right: transaction history */}
            <Col xl={7}>
              <Card className="card-one">
                <Card.Header className="pb-0">
                  <Card.Title as="h6" className="mb-0">
                    ประวัติรายการ
                    {selectedAcc && <span className="text-muted ms-2 fw-normal fs-sm">({selectedAcc.account_no})</span>}
                  </Card.Title>
                </Card.Header>
                <Card.Body className="p-0">
                  {txLoading ? (
                    <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
                  ) : (
                    <Table responsive hover className="mb-0 fs-sm">
                      <thead className="table-light">
                        <tr>
                          <th>วันที่</th>
                          <th>ประเภท</th>
                          <th className="text-end">จำนวน</th>
                          <th className="text-end">ยอดคงเหลือ</th>
                          <th>หมายเหตุ</th>
                          <th>บันทึกเมื่อ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!selectedAcc ? (
                          <tr><td colSpan={6} className="text-center text-muted py-4">เลือกบัญชีเพื่อดูรายการ</td></tr>
                        ) : transactions.length === 0 ? (
                          <tr><td colSpan={6} className="text-center text-muted py-4">ไม่มีรายการ</td></tr>
                        ) : transactions.map(t => (
                          <tr key={t.transaction_id}>
                            <td>{formatDateTH(t.occurred_date?.slice(0, 10))}</td>
                            <td>
                              <Badge bg={t.transaction_type === 'deposit' ? 'success' : 'danger'}>
                                {t.transaction_type === 'deposit' ? 'ฝาก' : 'ถอน'}
                              </Badge>
                            </td>
                            <td className={`text-end fw-semibold ${t.transaction_type === 'deposit' ? 'text-success' : 'text-danger'}`}>
                              {t.transaction_type === 'deposit' ? '+' : '-'}฿{fmt(t.amount)}
                            </td>
                            <td className="text-end">฿{fmt(t.balance_after)}</td>
                            <td>{t.note || <span className="text-muted">-</span>}</td>
                            <td className="text-muted">{formatDateTimeTH(t.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <Footer />
    </>
  )
}
